/**
 * Internal hook: manages auth session, social swap, and session bootstrap.
 * Extracted from UserContext to reduce file size.
 */
import { useState, useEffect, useRef } from 'react';
import { isBrowser } from '@/utils/environmentSafety';
import { supabase } from '@/integrations/supabase/client';
import { authService, mapDbPersonaToFrontend } from '@/services/supabase';
import { syncBiometricSessionToken } from '@/services/biometricService';
import { isLinkingInProgress, completeSocialLink } from '@/services/supabase/accountLinkingService';
import { getLinkingIntent, clearLinkingIntent, debugLinkingIntentSnapshot, LINKING_INFLIGHT_MS } from '@/utils/linkingIntent';
import { getOAuthIntent } from '@/utils/oauthIntent';
import { personaBelongsTo } from '@/utils/personaOwner';
import { captureError } from '@/utils/captureError';
import { tryAcquireSwapMint, releaseSwapMint } from '@/utils/swapMintLock';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import type { User, UserPersona } from './types';

interface AuthStateReturn {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  persona: UserPersona | null;
  setPersona: React.Dispatch<React.SetStateAction<UserPersona | null>>;
  session: Session | null;
  setSession: React.Dispatch<React.SetStateAction<Session | null>>;
  isAuthenticated: boolean;
  isLoading: boolean;
  isNewUser: boolean;
  setIsNewUser: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useAuthState(
  loadUserProfile: (supabaseUser: SupabaseUser) => Promise<void>
): AuthStateReturn {
  const [user, setUser] = useState<User | null>(null);
  const [persona, setPersona] = useState<UserPersona | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem('behtari_persona');
    const valid = ['job-seeker', 'individual-employer', 'business-employer', 'provider', 'agency', 'admin'];
    return (stored && valid.includes(stored)) ? stored as UserPersona : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return !localStorage.getItem('behtari_last_login_at');
  });
  const [session, setSession] = useState<Session | null>(null);
  const isAuthenticated = !!session;

  // Refs for loadUserProfile callback to avoid stale closures
  const loadProfileRef = useRef(loadUserProfile);
  loadProfileRef.current = loadUserProfile;

  // Track session ref for visibility handler
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const isInvalidRefreshTokenError = (message?: string | null) =>
    !!message && /invalid refresh token|refresh token not found|refresh_token_not_found|user_not_found|user from sub claim in jwt does not exist|missing sub claim|bad_jwt/i.test(message);

  const clearBrokenSession = async () => {
    try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* no-op */ }
    try {
      if (typeof window !== 'undefined') {
        Object.keys(window.localStorage)
          .filter((k) => k.startsWith('sb-') && k.includes('-auth-token'))
          .forEach((k) => window.localStorage.removeItem(k));
      }
    } catch { /* no-op */ }
    setSession(null);
    setUser(null);
    setPersona(null);
  };

  useEffect(() => {
    if (!isBrowser) {
      setIsLoading(false);
      return;
    }

    let swapInProgress = false;
    let swappedProviderUserId: string | null = null;
    let linkingCompleting = false;
    let hiddenAt: number | null = null;

    const maybeSwapSocialSession = async (currentSession: Session): Promise<boolean> => {
      if (swapInProgress || linkingCompleting) return true;
      if (isLinkingInProgress()) return false;

      // Any explicit OAuth intent must be owned by the centralized finalizer.
      // Running swap inside the auth callback can block Supabase setSession(),
      // which leaves the login button spinning even though Google created a session.
      // Use the shared helper (sessionStorage + localStorage fallback) — Safari iOS,
      // in-app webviews, and Apple's redirect can drop sessionStorage across the
      // round-trip, so a raw sessionStorage read would miss a real in-flight intent.
      const oauthIntent = getOAuthIntent();
      if (oauthIntent) return false;

      // Fast path: if the finalizer (finalizeOAuthSession) has already run for this
      // session, it writes behtari_persona to localStorage before navigating.
      // A stored persona means the user is an established, confirmed OAuth user —
      // the swap is not applicable and we can skip the profile network round-trip.
      // This closes the race where setIsLoading(false) was blocked waiting for the
      // profile fetch, causing the "stuck on signin loading" symptom.
      //
      // SCOPED TO THE CURRENT USER (2026-07-29). `behtari_persona` is written by
      // a dozen call sites and survives across accounts on a shared device, so an
      // unscoped presence check could not distinguish "this device already
      // finalized THIS account" from "a different account was signed in here
      // earlier" — and a leftover value silently suppressed the swap for a real
      // un-swapped OAuth session. The finalizer now stamps the owning auth user
      // id next to the persona; only a stamp matching this session short-circuits.
      // An absent/mismatched stamp just means "take the normal path" — never fatal.
      const storedPersona = (() => {
        try { return localStorage.getItem('behtari_persona'); } catch { return null; }
      })();
      if (storedPersona && personaBelongsTo(currentSession.user.id)) {
        swapInProgress = false;
        return false;
      }

      const provider = currentSession.user.app_metadata?.provider;
      if (provider !== 'google' && provider !== 'apple') return false;
      if (swappedProviderUserId === currentSession.user.id) return false;

      swapInProgress = true;

      const { profile } = await authService.getProfile(currentSession.user.id);
      if (profile?.cnic) {
        swapInProgress = false;
        return false;
      }

      swappedProviderUserId = currentSession.user.id;
      const providerEmail = currentSession.user.email;

      // `social-login-swap` REGENERATES the target account's one-time token on
      // every call, so this legacy path minting while finalizeOAuth is mid-swap
      // voids the token the finalizer is about to redeem — GoTrue then answers
      // "Email link is invalid or has expired" on a token seconds old, which is
      // exactly what was captured on 2026-08-05. The `getOAuthIntent()` guard
      // above does not cover it: that intent carries a 10-minute TTL, and any
      // native round-trip that outlives it (slow Custom Tab, account picker,
      // 2FA) drops the guard while the finalizer still owns the swap.
      //
      // Losing the lock is not an error — the finalizer is the designated owner
      // and is already doing this work. Fall through to the normal non-swap
      // path and let it finish. See `swapMintLock.ts`.
      const mintOwner = 'use-auth-state';
      if (providerEmail && tryAcquireSwapMint(mintOwner)) {
        try {
          const { data, error: fnError } = await supabase.functions.invoke(
            'social-login-swap',
            { body: { providerEmail: providerEmail.trim().toLowerCase(), provider } }
          );

          if (!fnError && data?.success && data?.hashedToken) {
            await supabase.auth.signOut({ scope: 'local' });

            const { error: otpError } = await supabase.auth.verifyOtp({
              token_hash: data.hashedToken,
              type: 'magiclink',
            });

            if (!otpError) {
              const { data: { session: cnicSession } } = await supabase.auth.getSession();
              swapInProgress = false;
              if (cnicSession?.user) {
                if (isBrowser) localStorage.removeItem('behtari_persona');
                setSession(cnicSession);
                if (cnicSession.refresh_token) {
                  syncBiometricSessionToken(cnicSession.user.id, cnicSession.refresh_token);
                }
                setTimeout(async () => {
                  try {
                    await loadProfileRef.current(cnicSession.user);
                  } catch {
                    // Profile load failed — still let user through
                  }
                  setIsLoading(false);
                }, 0);
              }
              return true;
            }
            console.error('[UserContext] verifyOtp error:', otpError);
          }
        } catch (swapErr) {
          console.error('[UserContext] social-login-swap error:', swapErr);
        } finally {
          releaseSwapMint(mintOwner);
        }
      }

      // Swap miss: under the new OAuth-signup policy, allow the OAuth session
      // through. /auth/callback (or the persona-selection redirect) will turn
      // it into a complete account. No more orphan cleanup / forced sign-out.
      swapInProgress = false;
      return false;
    };

    // Deferred linking completion — MUST run OUTSIDE the onAuthStateChange
    // callback. completeSocialLink() calls supabase.auth.signOut({scope:'local'})
    // and refreshSession(), each of which itself fires onAuthStateChange.
    // Awaiting them from inside a still-executing onAuthStateChange callback
    // reentrantly deadlocks supabase-js (the real "Page Unresponsive" freeze
    // reported on device) — the exact hazard the "Do NOT await profile loading
    // inside onAuthStateChange" note below warns about, and that the OTP-swap
    // path already sidesteps with setTimeout(…,0). Running here in a macrotask
    // lets the original callback return first; the intermediate reentrant events
    // (SIGNED_OUT from signOut, SIGNED_IN/TOKEN_REFRESHED from refreshSession)
    // are then swallowed by `if (linkingCompleting) return;` in the listener.
    const completeLinkingDeferred = async () => {
      try {
        const linkResult = await completeSocialLink();
        if (linkResult.success && linkResult.provider) {
          try {
            sessionStorage.setItem(
              'behtari_link_success',
              `${linkResult.provider}:${linkResult.email || ''}`
            );
          } catch { /* no-op */ }
        }
        const { data: { session: restoredSession } } = await supabase.auth.getSession();
        if (restoredSession?.user) {
          setSession(restoredSession);
          if (restoredSession.refresh_token) {
            syncBiometricSessionToken(restoredSession.user.id, restoredSession.refresh_token);
          }
          try {
            await loadProfileRef.current(restoredSession.user);
          } catch {
            // Profile load failed — still let the user through.
          }
        }
      } catch (err) {
        captureError('oauth-link-native', err, { stage: 'deferred-complete' });
      } finally {
        // Cleanup (unconditional): clear the flag from BOTH storage layers so a
        // failed/interrupted attempt can never leave it set for a future
        // unrelated sign-in to pick up. completeSocialLink() already clears it at
        // its start; this is the belt-and-suspenders guarantee for the paths
        // that return before that clear or throw.
        clearLinkingIntent();
        linkingCompleting = false;
        setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, currentSession) => {
        // Diagnostic trace — scoped to OAuth SIGNED_IN events where a linking
        // flag is actually present (either store, valid or stale) to avoid
        // PostHog noise. This exists because a real device link attempt (Majid
        // Ali Waryam, 2026-07-27) produced no auth_methods row AND zero
        // PostHog events under 'oauth-link-native' or 'oauth-link-deadlock-guard'
        // — meaning either this branch never ran, or it ran and hit a
        // THEN-silent guard clause (now instrumented in
        // accountLinkingService.completeSocialLink). This breadcrumb answers
        // "did the branch even see an in-flight linking flag" definitively on
        // the next device test, regardless of which of those it turns out to be.
        // Root cause of the noise (2026-07-29, round 1): the trace previously
        // fired on EVERY Google/Apple SIGNED_IN event — including plain,
        // unrelated sign-ins with no linking flag at all — via captureError
        // (PostHog captureException), so every normal OAuth login was filed as
        // an "Error" and repeatedly re-opened auto-fix issues (#57, #61). Fixed
        // by gating on raw flag PRESENCE in either store.
        // Root cause of the noise (2026-07-29, round 2 — #64): raw presence
        // is not the same as validity. debugLinkingIntentSnapshot() reports
        // `present: true` for a flag regardless of its 10-minute TTL, and NO
        // other code path ever clears a flag once it is older than that TTL —
        // isLinkingInProgress() (used everywhere else) already treats an
        // expired flag as absent, so the SIGNED_IN branch below that clears
        // stale-but-still-"in-progress" flags never runs for one this old.
        // Net effect: a single interrupted linking attempt left a permanent
        // garbage key in localStorage that re-tripped this trace on every
        // subsequent unrelated sign-in for that browser, forever. Fix: only
        // capture when the flag is actually valid (isValid — non-expired,
        // well-formed); when it is merely present-but-expired, clear it here
        // instead so it can never trip this trace (or linger in storage) again.
        if (_event === 'SIGNED_IN' && currentSession?.user) {
          const oauthProvider = currentSession.user.app_metadata?.provider;
          if (oauthProvider === 'google' || oauthProvider === 'apple') {
            const snapshot = debugLinkingIntentSnapshot();
            if (snapshot.isValid) {
              captureError('oauth-signed-in-trace', new Error('OAuth SIGNED_IN observed'), {
                provider: oauthProvider,
                signedInUserId: currentSession.user.id,
                isLinkingInProgress: snapshot.isValid,
                linkingFlagAgeMs: snapshot.ageMs,
                sessionStorageHasFlag: snapshot.sessionStorageHasFlag,
                localStorageHasFlag: snapshot.localStorageHasFlag,
                linkingCompleting,
              });
            } else if (snapshot.sessionStorageHasFlag || snapshot.localStorageHasFlag) {
              clearLinkingIntent();
            }
          }
        }

        if (_event === 'SIGNED_IN' && isLinkingInProgress()) {
          // Only a GENUINELY in-flight linking round-trip (flag stamped within
          // the last LINKING_INFLIGHT_MS) may drive the completion path. A real
          // OAuth round-trip finishes in seconds; an older flag is a leftover
          // from an interrupted attempt — and if allowed through, an ordinary
          // SIGNED_IN / session restore on page load would hit the signOut/
          // refreshSession reentrancy deadlock (Chrome "Page Unresponsive").
          const linkAgeMs = (() => {
            const li = getLinkingIntent();
            return li?.ts ? Date.now() - li.ts : Number.POSITIVE_INFINITY;
          })();

          if (linkAgeMs <= LINKING_INFLIGHT_MS) {
            // Fresh, real link-in-flight. Mark completing and DEFER the actual
            // completion out of this callback (setTimeout macrotask) so this
            // onAuthStateChange invocation returns BEFORE completeSocialLink()
            // calls signOut()/refreshSession() (which re-fire onAuthStateChange).
            // The callback must not await anything that can re-enter the listener.
            linkingCompleting = true;
            setTimeout(() => { void completeLinkingDeferred(); }, 0);
            return;
          }

          // Stale flag from an interrupted earlier attempt — clear it from both
          // stores and FALL THROUGH to normal handling (do NOT enter completion,
          // do NOT return). Capture so a recurrence (which previously froze the
          // page) is visible in PostHog instead of silently deadlocking again.
          clearLinkingIntent();
          captureError(
            'oauth-link-deadlock-guard',
            new Error('stale linking flag ignored on SIGNED_IN'),
            { linkAgeMs, thresholdMs: LINKING_INFLIGHT_MS }
          );
        }

        if (linkingCompleting) return;

        if ((_event === 'SIGNED_IN' || _event === 'INITIAL_SESSION') && currentSession?.user) {
          const didSwap = await maybeSwapSocialSession(currentSession);
          if (didSwap) return;
        }

        setSession(currentSession);
        if (currentSession?.user && currentSession?.refresh_token) {
          syncBiometricSessionToken(currentSession.user.id, currentSession.refresh_token);
        }

        if (currentSession?.user) {
          // Do NOT await profile loading inside onAuthStateChange — it can
          // cause a deadlock (Supabase auth-js known issue).  Set session
          // immediately so the app unblocks; profile loads in a separate effect.
          setIsLoading(false);
        } else {
          setUser(null);
          setPersona(null);
          setIsLoading(false);
        }
      }
    );

    // Safety timeout: if onAuthStateChange never fires INITIAL_SESSION within 5s,
    // force isLoading to false so users aren't stuck on a blank screen.
    const safetyTimer = setTimeout(() => {
      setIsLoading((current) => {
        if (current) {
          console.warn('[useAuthState] Safety timeout — forcing isLoading=false');
        }
        return false;
      });
    }, 5000);

    // --- Rehydrate session on app resume (visibility change / BFCache) ---
    const rehydrateSession = async () => {
      try {
        const { data: { session: freshSession }, error } = await supabase.auth.getSession();
        if (error && isInvalidRefreshTokenError(error.message)) {
          await clearBrokenSession();
          return;
        }
        if (freshSession?.user) {
          setSession(freshSession);
          if (freshSession.refresh_token) {
            syncBiometricSessionToken(freshSession.user.id, freshSession.refresh_token);
          }
          // Reload profile only if we had no session (recovered from backgrounding)
          if (!sessionRef.current) {
            await loadProfileRef.current(freshSession.user);
          }
        }
      } catch {
        // network error while backgrounded — keep current state, don't sign out
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible') {
        // Only rehydrate if the tab was actually hidden for >2s (real background,
        // not a DevTools open/close which toggles visibility for <100ms in some browsers).
        const wasHiddenLong = hiddenAt !== null && Date.now() - hiddenAt > 2000;
        hiddenAt = null;
        if (wasHiddenLong) {
          rehydrateSession();
        }
      }
    };

    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        rehydrateSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Separate effect: load profile whenever session changes (decoupled from onAuthStateChange)
  useEffect(() => {
    if (session?.user) {
      loadProfileRef.current(session.user).catch((err) => {
        console.warn('[useAuthState] Profile load failed (non-blocking):', err);
      });
    }
  }, [session?.user?.id]);

  return {
    user, setUser,
    persona, setPersona,
    session, setSession,
    isAuthenticated,
    isLoading,
    isNewUser, setIsNewUser,
  };
}
